import { fetchOpenMeteo } from './open-meteo-client.js';
import { ageMinutesFrom, readWeatherCache, writeWeatherCache } from './weather-cache.js';
import type { WeatherBundle, WeatherViewModel } from './weather-types.js';

const MIN_FETCH_INTERVAL_MS = 5 * 60 * 1000;
const STALE_OK_MINUTES = 90;

let lastFetchAttempt = 0;
let inFlight: Promise<WeatherViewModel> | null = null;

function viewFromBundle(bundle: WeatherBundle, status: 'live' | 'stale'): WeatherViewModel {
  return {
    status,
    bundle,
    ageMinutes: ageMinutesFrom(bundle.fetchedAt),
    errorMessage: null,
  };
}

function unavailable(errorMessage: string, cached: WeatherBundle | null): WeatherViewModel {
  if (cached) {
    const age = ageMinutesFrom(cached.fetchedAt);
    return {
      status: 'stale',
      bundle: cached,
      ageMinutes: age,
      errorMessage: `${errorMessage} (mostrando último dato válido)`,
    };
  }
  return { status: 'unavailable', bundle: null, ageMinutes: null, errorMessage };
}

export async function loadWeather(options?: { force?: boolean }): Promise<WeatherViewModel> {
  const force = options?.force === true;
  const now = Date.now();
  const cached = readWeatherCache();

  if (!force && cached && ageMinutesFrom(cached.bundle.fetchedAt) < STALE_OK_MINUTES) {
    const age = ageMinutesFrom(cached.bundle.fetchedAt);
    const status = age <= 20 ? 'live' : 'stale';
    if (now - lastFetchAttempt >= MIN_FETCH_INTERVAL_MS) {
      void loadWeather({ force: true });
    }
    return viewFromBundle(cached.bundle, status);
  }

  if (!force && now - lastFetchAttempt < MIN_FETCH_INTERVAL_MS && cached) {
    return viewFromBundle(cached.bundle, 'stale');
  }

  if (inFlight) return inFlight;

  lastFetchAttempt = now;
  inFlight = (async () => {
    try {
      const bundle = await fetchOpenMeteo();
      writeWeatherCache(bundle);
      return viewFromBundle(bundle, 'live');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'error_desconocido';
      const fallback = readWeatherCache()?.bundle ?? cached?.bundle ?? null;
      return unavailable(msg, fallback);
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Solo para tests — reinicia límites en memoria. */
export function resetWeatherServiceForTests(): void {
  lastFetchAttempt = 0;
  inFlight = null;
}
